import type { EmailDomainSetup, IDXSiteSetupData } from '../schemas/onboarding.schema';

type SiteData = Partial<IDXSiteSetupData> | undefined;

/** The domain Remi's IDX sits under, e.g. "abcrealty.com". */
export const remiIdxDomain = (siteData: SiteData): string =>
  ((siteData?.has_website === 'no' ? siteData?.new_domain : siteData?.website_domain) ?? '').trim();

/** Remi's IDX site host from the original IDX setup, e.g. "homes.abcrealty.com". */
export const remiIdxSiteDomain = (siteData: SiteData): string => {
  const subdomain = siteData?.idx_subdomain?.trim();
  const domain = remiIdxDomain(siteData);
  return subdomain && domain ? `${subdomain}.${domain}` : '';
};

/**
 * Remi's email setup on the "Create Remi's IDX for me" path: the IDX domain or
 * another domain the client entered. Its DNS records are added the same way as
 * the IDX record (by the client, or by Speculo with the IDX domain access).
 */
export const remiIdxEmailDomain = (siteData: SiteData): EmailDomainSetup => {
  const sameAsIdx = siteData?.email_domain_source !== 'different';
  const domain = sameAsIdx ? remiIdxDomain(siteData) : (siteData?.email_domain ?? '').trim();
  const speculoSetsUp = siteData?.needs_domain_help === true;
  return {
    setup_option: speculoSetsUp ? 'speculo' : 'self',
    domain_source: sameAsIdx ? 'same_as_idx' : 'different',
    domain,
    ...(sameAsIdx && { sendgrid_confirmed: siteData?.sendgrid_confirmed === true }),
    ...(speculoSetsUp
      ? siteData?.domain_access_shared
        ? { access_method: 'godaddy_delegate', godaddy_access_confirmed: true }
        : {
            access_method: 'credentials',
            provider: siteData?.provider,
            ...(siteData?.provider === 'other' && {
              provider_other_name: siteData?.provider_other_name,
            }),
            ...(siteData?.provider !== 'godaddy' && { login_url: siteData?.login_url }),
            two_fa_contact_type: siteData?.two_fa_contact_type,
            two_fa_contact_name: siteData?.two_fa_contact_name,
            two_fa_contact_email: siteData?.two_fa_contact_email,
            two_fa_contact_phone: siteData?.two_fa_contact_phone,
          }
      : { dns_records_domain: domain }),
  };
};

/**
 * The IDX Site Setup answers as saved on the organization: no uploaded files,
 * no registrar login (that goes to Secret Manager), and compliance links as
 * `{ title, url }`, since Remi API rejects org fields named like credentials
 * (such as `key`).
 */
export const toStoredSiteData = (siteData: Record<string, unknown>) => {
  const {
    logo_file: _logoFile,
    favicon_file: _faviconFile,
    registrar_username: _registrarUsername,
    registrar_password: _registrarPassword,
    compliance_links: complianceLinks,
    ...rest
  } = siteData;
  return {
    ...rest,
    ...(Array.isArray(complianceLinks) && {
      compliance_links: (complianceLinks as { key: string; value: string }[]).map((link) => ({
        title: link.key,
        url: link.value,
      })),
    }),
  };
};
